import { defineStore } from 'pinia'
import api from '../api'
import moment from 'moment'
import {
  getCurrentClinic,
  getCurrentUser,
  userHasPermission,
  getScheduleOptions as getSavedScheduleOptions,
  setScheduleOptions as saveScheduleOptions
} from '@/utils/localStorageManager'
import type { ScheduleResource, CalendarEvent, ScheduleState } from '../types'

function getEventStartTime(appointment: any): string {
  return (
    (appointment.clinic?.attendance_type === 'ARRIVAL' &&
      appointment.start_datetime) ||
    appointment.start_datetime
  )
}

function getEventEndTime(appointment: any): string {
  return appointment.end_datetime
}

function extractPlans(appointmentItems: any[]): string[] {
  if (!appointmentItems?.length) return []
  return [
    ...new Set(
      appointmentItems.map(
        ai => ai?.health_plan?.fantasy_name ?? ai?.health_plan?.company_name
      )
    )
  ]
}

export const useScheduleStore = defineStore('schedule', {
  state: () => ({
    schedules: [],
    selectedSchedules: [],
    loadingAppointments: false,
    pendingAppointmentsRequest: null,
    events: [],
    surgicalEvents: [],
    appointmentRefreshTrigger: 0
  }),

  actions: {
    async getAppointments(clinicId, props) {
      if (this.loadingAppointments) {
        this.pendingAppointmentsRequest = { clinicId, props }
        return
      }

      if (!clinicId || !props?.professionalIds?.length) {
        this.events = []
        return
      }

      this.loadingAppointments = true

      try {
        const { data } = await api.getAppointments(clinicId, props)
        const events = []

        data.forEach(appointment => {
          events.push({
            id: appointment.id,
            title: appointment.patient?.name ?? (
              appointment.type === 'Reserva'
                ? (appointment.product_origin === 'marketplace' ? 'Reservado - Marketplace' : 'Reservado - BI')
                : 'Sem paciente'
            ),
            start: getEventStartTime(appointment),
            end: getEventEndTime(appointment),
            appointment,
            resourceId: appointment.professional_id || appointment.room_id,
            plans: extractPlans(appointment.appointment_items)
          })

          // Cria evento adicional na sala quando o agendamento tem profissional E sala.
          const roomInSchedule =
            this.selectedSchedules?.some(s => s.id === appointment.room_id) ??
            false

          if (
            appointment.room_id &&
            appointment.professional_id &&
            appointment.room_id !== appointment.professional_id &&
            roomInSchedule
          ) {
            events.push({
              id: appointment.id + '_room',
              title: appointment.patient?.name ?? (
                appointment.type === 'Reserva'
                  ? (appointment.product_origin === 'marketplace' ? 'Reservado - Marketplace' : 'Reservado - BI')
                  : 'Sem paciente'
              ),
              start: getEventStartTime(appointment),
              end: getEventEndTime(appointment),
              appointment,
              resourceId: appointment.room_id,
              plans: extractPlans(appointment.appointment_items)
            })
          }
        })

        this.events = events
      } finally {
        this.loadingAppointments = false
        const pending = this.pendingAppointmentsRequest
        if (pending) {
          this.pendingAppointmentsRequest = null
          this.getAppointments(pending.clinicId, pending.props)
        }
      }
    },

    async getSurgicalMapAppointments({ start, filters, currentUnityId }) {
      const clinic = getCurrentClinic()
      this.loadingAppointments = true
      const formattedStart = moment(start).format('YYYY-MM-DD')
      const clinicId = currentUnityId || clinic.id

      try {
        const [{ data: surgicalMapData }, { data: roomReservationData }] =
          await Promise.all([
            api.getSurgicalMapData(clinicId, formattedStart, filters),
            api.getRoomReservationByDay(clinicId, formattedStart, filters)
          ])

        const events = surgicalMapData.map(event => ({
          id: event.id,
          title: event.patient?.name,
          start: event.start_datetime,
          end: event.end_datetime,
          resourceId: event.room_id,
          appointment: event,
          plans: extractPlans(event.appointment_items)
        }))

        const roomReservations = roomReservationData.map(event => ({
          id: event.id,
          type: 'reservation',
          start: `${event.date} ${event.start_time}`,
          end: `${event.date} ${event.end_time}`,
          resourceId: event.room_id,
          data: {
            ...event,
            title: event.professional?.name
              ? 'Sala reservada'
              : 'Sala bloqueada',
            type: event.professional?.name ? 'reservation' : 'block'
          }
        }))

        this.surgicalEvents = [...events, ...roomReservations]
      } catch (error) {
        console.error('getSurgicalMapAppointments error:', error)
      } finally {
        this.loadingAppointments = false
      }
    },

    // Backward compat alias (typo used by Surgery Center components)
    async getSurgicalMapAppoinments(params) {
      return this.getSurgicalMapAppointments(params)
    },

    async getScheduleOptions(clinicId) {
      const user = getCurrentUser()
      let managers = []

      try {
        const { data } = await api.getProfessionalsAndRooms(clinicId)
        let filtered = [...data]

        filtered.sort((a, b) => {
          if (a.name < b.name) return -1
          if (a.name > b.name) return 1
          return 0
        })

        if (user.type === 'DOCTOR' && userHasPermission('FpAgenSal10')) {
          filtered = data.filter(el => el.id === user.id || el.role === 'room')
        }

        this.schedules = filtered

        if (user.type === 'DOCTOR') {
          const schedule = filtered.find(el => el.id === user.id)
          if (
            schedule &&
            !managers.some(manager => manager.id === schedule.id)
          ) {
            managers.push(schedule)
          }
        } else {
          managers = data.reduce((acc, schedule) => {
            if (
              acc.length < 3 &&
              schedule.role === 'DOCTOR_MANAGER' &&
              !acc.some(manager => manager.id === schedule.id)
            ) {
              acc.push(schedule)
            }
            return acc
          }, [])
        }

        const scheduleOptions = getSavedScheduleOptions()
        const savedSchedules = scheduleOptions.filter(savedSchedule =>
          filtered.some(
            availableSchedule => availableSchedule.id === savedSchedule.id
          )
        )

        this.setSelectedSchedules(
          savedSchedules.length ? savedSchedules : managers
        )
      } catch (error) {
        console.error(error)
      }
    },

    setSelectedSchedules(payload) {
      saveScheduleOptions(payload)
      this.selectedSchedules = payload
    },

    refreshAppointments() {
      this.appointmentRefreshTrigger = (this.appointmentRefreshTrigger || 0) + 1
    }
  }
})
